import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSession } from "../lib/auth.ts";
import { api, formatSerial } from "../lib/api.ts";
import { BatteryIcon, EmptyState } from "../components/EmptyState.tsx";
import { useSortableTable, SortHeader } from "../components/SortableTable.tsx";

export function DevicesPage() {
  const { data: session } = useSession();
  const orgId = session?.session.activeOrganizationId;
  const nav = useNavigate();
  const devices = useQuery({
    queryKey: ["devices", orgId],
    queryFn: () => api.listDevices(orgId!),
    enabled: !!orgId,
  });
  const sortable = useSortableTable(devices.data?.devices ?? [], {
    name:            (d) => d.name,
    assetSerial:     (d) => d.assetSerial,
    serial:          (d) => d.serial,
    updateChannel:   (d) => d.updateChannel,
    firmwareVersion: (d) => d.firmwareVersion,
    pairedAt:        (d) => d.pairedAt,
    lastSeen:        (d) => d.lastSeen,
  }, "pairedAt", "desc");

  return (
    <>
      <h1>Devices</h1>
      {devices.isLoading && <p className="muted">Loading…</p>}
      {devices.error && (
        <p className="err">Couldn't load devices — {(devices.error as Error).message}</p>
      )}
      {devices.data && devices.data.devices.length === 0 && (
        <EmptyState
          icon={<BatteryIcon />}
          title="No devices yet"
          message="On a tester, open Settings → Cloud and scan the pairing QR code to add it here."
        />
      )}
      {devices.data && devices.data.devices.length > 0 && (
        <table>
          <thead>
            <tr>
              <SortHeader label="Name"         columnKey="name"            sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Asset serial" columnKey="assetSerial"     sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Serial"       columnKey="serial"          sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Channel"      columnKey="updateChannel"   sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Firmware"  columnKey="firmwareVersion" sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Paired"    columnKey="pairedAt"        sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Last seen" columnKey="lastSeen"        sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
            </tr>
          </thead>
          <tbody>
            {sortable.sorted.map((d) => (
              <tr key={d.id} onClick={() => nav(`/devices/${d.id}`)}>
                <td>{d.name ?? <span className="muted">(unnamed)</span>}</td>
                <td>{d.assetSerial ? <code>{d.assetSerial}</code> : <span className="muted">—</span>}</td>
                <td><code>{formatSerial(d.serial)}</code></td>
                <td>{d.updateChannel}</td>
                <td>{d.firmwareVersion ?? <span className="muted">—</span>}</td>
                <td>{new Date(d.pairedAt).toLocaleDateString()}</td>
                <td>
                  {d.lastSeen
                    ? new Date(d.lastSeen).toLocaleString()
                    : <span className="muted">never</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
